import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import {
  parseFilenameFromContentDisposition,
  triggerBlobDownload,
} from '@/shared/lib/downloadBlob'
import { toCommaQueryParam } from './commaQueryParam'
import {
  PACKAGING_SUPPLIERS_KEY,
  type PackagingFieldCrossCheckIssueCode,
  type PackagingSupplierComplianceProgress,
  type PackagingSupplierOnboardingStatus,
} from './packagingSuppliers'
import type { PackagingItemAuditStatus } from './packagingItemsOverview'

export type PageInfo = {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export type PackedItemRedFlagDetail = {
  code: PackagingFieldCrossCheckIssueCode
  packagingItemId: string
  packagingItemName: string
  reasoningEn: string
  reasoningZh: string
}

export type PackagedItemOverviewPackagingItemAudit = {
  auditStatus: PackagingItemAuditStatus
  auditIssues: Array<{
    code: PackagingFieldCrossCheckIssueCode
    reasoningEn: string
    reasoningZh: string
  }>
  auditLastRunAt: string | null
}

export type PackedItemsOverviewPackagingItem = {
  id: string
  name: string
  completedAt: string | null
} & PackagedItemOverviewPackagingItemAudit

export type PackedItemsOverviewItem = {
  id: string
  name: string
  sku: string
  supplierId: string
  supplierName: string
  notInSourceList: boolean
  fromPartsCatalog: boolean
  complianceProgress: PackagingSupplierComplianceProgress
  completedPackagingItemCount: number
  totalPackagingItemCount: number
  redFlags: PackagingFieldCrossCheckIssueCode[]
  redFlagDetails: PackedItemRedFlagDetail[]
  packagingItems: PackedItemsOverviewPackagingItem[]
}

/** @deprecated Nested supplier tree removed; use PackedItemsOverviewItem */
export type PackedItemsOverviewPackedItem = Omit<
  PackedItemsOverviewItem,
  'supplierId' | 'supplierName'
>

/** @deprecated Nested supplier tree removed */
export type PackedItemsOverviewSupplier = {
  id: string
  name: string
  onboardingStatus: PackagingSupplierOnboardingStatus
  packedItemCount: number
  packedItems: PackedItemsOverviewPackedItem[]
}

export type PackedItemsOverviewResponse = {
  partsCatalogConfigured: boolean
  items: PackedItemsOverviewItem[]
  pageInfo: PageInfo
}

export type PackedItemsOverviewQuery = {
  page?: number
  pageSize?: number
  searchQuery?: string
  sku?: string
  audit?: PackagingItemAuditStatus[]
  notInSourceList?: boolean
  sortBy?: PackedItemsOverviewSortBy
  sortDir?: PackedItemsOverviewSortDir
}

export type PackedItemsOverviewSortBy =
  | 'name'
  | 'sku'
  | 'supplier'
  | 'complianceProgress'
  | 'audit'

export type PackedItemsOverviewSortDir = 'asc' | 'desc'

export type PackedItemsOverviewExportQuery = Omit<
  PackedItemsOverviewQuery,
  'page' | 'pageSize'
>

export type PackedItemsOverviewExportResponse = {
  items: PackedItemsOverviewItem[]
}

export type PackedItemsFunnelStats = {
  total: number
  unaddressed: number
  unaddressedPercent: number
  inProgress: number
  inProgressPercent: number
  completed: number
  completedPercent: number
}

export type PackedItemDetailPackagingItem = {
  id: string
  name: string
  completedAt: string | null
} & PackagedItemOverviewPackagingItemAudit

export type PackedItemDetail = {
  id: string
  name: string
  description: string
  sku: string
  notInSourceList: boolean
  fromPartsCatalog: boolean
  hasNoPackaging: boolean
  supplier: {
    id: string
    name: string
  }
  complianceProgress: PackagingSupplierComplianceProgress
  completedPackagingItemCount: number
  totalPackagingItemCount: number
  redFlags: PackagingFieldCrossCheckIssueCode[]
  redFlagDetails: PackedItemRedFlagDetail[]
  packagingItems: PackedItemDetailPackagingItem[]
}

export function useListPackedItemsOverview(
  query: PackedItemsOverviewQuery = {},
  enabled = true
) {
  return useQuery<PackedItemsOverviewResponse, Error>({
    queryKey: [PACKAGING_SUPPLIERS_KEY, 'packed-items-overview', query],
    queryFn: async () => {
      const response = await apiClient.get<PackedItemsOverviewResponse>(
        '/v1/packaging-suppliers/packed-items-overview',
        {
          params: {
            page: query.page,
            pageSize: query.pageSize,
            q: query.searchQuery,
            sku: query.sku,
            audit: toCommaQueryParam(query.audit),
            notInSourceList:
              query.notInSourceList === true ? 'true' : undefined,
            sortBy: query.sortBy,
            sortDir: query.sortDir,
          },
        }
      )
      return response.data
    },
    enabled,
    placeholderData: keepPreviousData,
  })
}

export async function downloadPackedItemsOverviewExport(
  query: PackedItemsOverviewExportQuery = {},
  fallbackFilename: string
): Promise<void> {
  const response = await apiClient.get<Blob>(
    '/v1/packaging-suppliers/packed-items-overview/export',
    {
      params: {
        q: query.searchQuery,
        sku: query.sku,
        audit: toCommaQueryParam(query.audit),
        notInSourceList: query.notInSourceList === true ? 'true' : undefined,
        sortBy: query.sortBy,
        sortDir: query.sortDir,
      },
      responseType: 'blob',
      timeout: 120_000,
    }
  )
  const filename = parseFilenameFromContentDisposition(
    response.headers['content-disposition'],
    fallbackFilename
  )
  triggerBlobDownload(response.data, filename)
}

export function useListPackedItemsStats(enabled = true) {
  return useQuery<PackedItemsFunnelStats, Error>({
    queryKey: [PACKAGING_SUPPLIERS_KEY, 'packed-items-stats'],
    queryFn: async () => {
      const response = await apiClient.get<PackedItemsFunnelStats>(
        '/v1/packaging-suppliers/packed-items-stats'
      )
      return response.data
    },
    enabled,
  })
}

export function useMarkPackedItemHasNoPackaging(packedItemId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (hasNoPackaging: boolean) => {
      const response = await apiClient.patch<PackedItemDetail>(
        `/v1/packaging-suppliers/packaged-items/${packedItemId}`,
        { hasNoPackaging }
      )
      return response.data
    },
    onSuccess: async (packedItem) => {
      queryClient.setQueryData(
        [PACKAGING_SUPPLIERS_KEY, 'packed-item', packedItemId],
        packedItem
      )
      await queryClient.invalidateQueries({
        queryKey: [PACKAGING_SUPPLIERS_KEY],
      })
    },
  })
}

export function useGetPackedItem(packedItemId: string | undefined) {
  return useQuery<PackedItemDetail, Error>({
    queryKey: [PACKAGING_SUPPLIERS_KEY, 'packed-item', packedItemId],
    queryFn: async () => {
      const response = await apiClient.get<PackedItemDetail>(
        `/v1/packaging-suppliers/packaged-items/${packedItemId}`
      )
      return response.data
    },
    enabled: Boolean(packedItemId),
  })
}
